import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Building2, 
  TrendingUp,
  TrendingDown,
  FileText,
  Download,
  Upload,
  Clock,
  CheckCircle2,
  Info,
  User,
  BarChart3,
  Receipt,
  ChevronDown,
  ChevronUp,
  Eye,
  ExternalLink
} from 'lucide-react';
import api, { unidadesApi, resultadosApi, cotizacionesApi, archivosApi, contratosApi, expensasApi } from '../../lib/api';
import { 
  formatCurrency, 
  formatPeriodo, 
  formatNumber, 
  getStatusLabel,
  cn
} from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { StatusBadge } from '../../components/common/StatusBadge';
import { PeriodSelector } from '../../components/common/PeriodSelector';
import { LoadingState, EmptyState, ErrorState } from '../../components/common/EmptyState';
import { ExpensasEstadoCuenta, ExpensasResumenDeuda } from '../../components/propietario/ExpensasEstadoCuenta';
import { CoworkInversorView } from '../../components/cowork/CoworkInversorView';
import { toast } from 'sonner';

// Tabs por sección del portal.
// Renta: Resumen · Facturación y Cobro · Inquilino (Rentabilidad oculta temporalmente).
// Expensas: Detalle · Estado de cuenta.
const TABS_BY_MODE = {
  renta: [
    { id: 'resumen', label: 'Resumen' },
    { id: 'facturacion', label: 'Facturación y Cobro' },
    { id: 'inquilino', label: 'Inquilino' },
  ],
  expensas: [
    { id: 'detalle', label: 'Detalle' },
    { id: 'pagos', label: 'Pagos' },
    { id: 'estado_cuenta', label: 'Estado de cuenta' },
  ],
};

export const PropietarioUnidadDetalle = ({ mode = 'renta', unidadIdProp = null, previewPropietarioId = null, previewMode = false }) => {
  const { unidadId: unidadIdParam } = useParams();
  const unidadId = unidadIdProp || unidadIdParam;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const tabsBase = TABS_BY_MODE[mode] || TABS_BY_MODE.renta;

  const [unidad, setUnidad] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [cotizaciones, setCotizaciones] = useState({});
  const [selectedPeriodo, setSelectedPeriodo] = useState(null);
  const [activeTab, setActiveTab] = useState(mode === 'expensas' ? 'detalle' : 'resumen');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingFactura, setUploadingFactura] = useState(null);
  const [showEgresosDetalle, setShowEgresosDetalle] = useState(false);
  const [egresosDetalle, setEgresosDetalle] = useState(null);
  const [loadingEgresos, setLoadingEgresos] = useState(false);
  const [chartMonths, setChartMonths] = useState(12);
  const [showIngresoDetalle, setShowIngresoDetalle] = useState(false);

  useEffect(() => { fetchData(); }, [unidadId, previewPropietarioId]);

  const getMesAnterior = () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 12 : now.getMonth();
    return `${year}-${String(month).padStart(2, '0')}`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [unidadRes, resultadosRes, cotizacionesRes, contratosRes] = await Promise.all([
        unidadesApi.get(unidadId),
        resultadosApi.list(
          previewMode && previewPropietarioId
            ? { unidad_id: unidadId, preview_propietario_id: previewPropietarioId }
            : { unidad_id: unidadId }
        ),
        cotizacionesApi.list(),
        contratosApi.list({ unidad_id: unidadId }),
      ]);
      setUnidad(unidadRes.data);
      setResultados(resultadosRes.data);
      const contratoVigente = contratosRes.data.find(c => c.estado === 'vigente');
      setContrato(contratoVigente || null);
      const cotizacionesMap = {};
      cotizacionesRes.data.forEach(c => { cotizacionesMap[c.periodo] = c.usd_ars; });
      setCotizaciones(cotizacionesMap);
      if (resultadosRes.data.length > 0) {
        setSelectedPeriodo(resultadosRes.data[0].periodo);
      }
    } catch (err) {
      setError('Error al cargar los datos');
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const selectedResultado = resultados.find(r => r.periodo === selectedPeriodo);
  const availablePeriods = resultados.map(r => r.periodo);
  const cotizacionActual = cotizaciones[selectedPeriodo] || 1100;

  const getGananciaNeta = (resultado) => {
    if (!resultado) return null;
    return resultado.usa_manual && resultado.ganancia_neta_manual
      ? resultado.ganancia_neta_manual
      : resultado.ganancia_neta_calculada;
  };

  const gananciaActual = getGananciaNeta(selectedResultado);

  const calcularRentabilidadAnualUSD = (resultado) => {
    if (!resultado || !unidad?.monto_inversion?.monto) return null;
    const ganancia = getGananciaNeta(resultado);
    if (!ganancia?.monto) return null;
    const rentaMensualUSD = ganancia.moneda === 'USD' ? ganancia.monto : ganancia.monto / (cotizaciones[resultado.periodo] || cotizacionActual);
    const inversionUSD = unidad.monto_inversion.moneda === 'USD' ? unidad.monto_inversion.monto : unidad.monto_inversion.monto / cotizacionActual;
    return (rentaMensualUSD * 12 / inversionUSD) * 100;
  };

  const rentabilidadAnualUSD = calcularRentabilidadAnualUSD(selectedResultado);

  const abrirFacturaServicio = async (servicio, idx) => {
    try {
      const r = await api.get(`/egresos/unidad/${unidadId}/factura-servicio`, { params: { periodo: selectedPeriodo, servicio, idx }, responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch {
      toast.error('No se pudo abrir la factura');
    }
  };

  const toggleEgresosDetalle = async () => {
    if (showEgresosDetalle) {
      setShowEgresosDetalle(false);
      return;
    }
    if (!selectedPeriodo || !unidadId) return;
    setLoadingEgresos(true);
    try {
      const res = await resultadosApi.getEgresosUnidad(unidadId, selectedPeriodo);
      setEgresosDetalle(res.data);
    } catch { setEgresosDetalle(null); }
    finally { setLoadingEgresos(false); }
    setShowEgresosDetalle(true);
  };

  // Reset detalle when period changes
  useEffect(() => { setShowEgresosDetalle(false); setEgresosDetalle(null); setShowIngresoDetalle(false); }, [selectedPeriodo]);

  const handleFileUpload = async (e, resultadoId) => {
    const file = e.target.files?.[0];
    if (!file || !resultadoId) return;
    setUploadingFactura(resultadoId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await resultadosApi.uploadFacturaPropietario(resultadoId, formData);
      toast.success('Factura cargada');
      fetchData();
    } catch (err) {
      toast.error('Error al cargar la factura');
    } finally {
      setUploadingFactura(null);
    }
  };

  if (loading) return <LoadingState message="Cargando información..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;
  if (!unidad) return <EmptyState icon={Building2} title="Unidad no encontrada" />;

  // ═══════════════════════ RENDER TABS ═══════════════════════

  const renderResumen = () => (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="border-b border-slate-100 px-3 md:px-5 py-2.5 md:py-3 flex items-center justify-between gap-2">
        {availablePeriods.length > 0 && (
          <PeriodSelector value={selectedPeriodo} onChange={setSelectedPeriodo} periods={availablePeriods} placeholder="Período" />
        )}
        {rentabilidadAnualUSD !== null && (
          <span className="text-xs text-slate-500 font-medium">
            Rent. anual <span className="text-[#1B4D3E] font-semibold">{rentabilidadAnualUSD.toFixed(1)}%</span> USD
          </span>
        )}
      </div>
      {selectedResultado ? (
        <div className="px-3 md:px-5 py-2.5 md:py-4">
          <div className="space-y-0">
            {/* Ingreso - desplegable si hay grupo */}
            {(() => {
              const grupoIngresos = selectedResultado.grupo_ingresos;
              const hasGrupo = grupoIngresos && grupoIngresos.length > 1;
              // Sumar SOLO las unidades no consolidadas: una cochera componente cuyo
              // ingreso ya está dentro de la oficina no debe sumarse (evita doble conteo).
              const sumables = hasGrupo ? grupoIngresos.filter(g => !g.consolidada) : [];
              const totalIngreso = hasGrupo
                ? sumables.reduce((s, g) => s + (g.ingreso_monto || 0), 0)
                : selectedResultado.ingreso?.monto;
              return (
                <div>
                  <button
                    onClick={() => hasGrupo && setShowIngresoDetalle(!showIngresoDetalle)}
                    className={cn("w-full flex items-center justify-between py-2 border-b border-slate-50", hasGrupo && "hover:bg-slate-50/50 cursor-pointer")}
                    disabled={!hasGrupo}
                  >
                    <span className="text-xs md:text-sm text-slate-600 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"></span>Ingreso
                      {hasGrupo && (showIngresoDetalle ? <ChevronUp className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />)}
                    </span>
                    <span className="text-sm md:text-base font-semibold font-mono text-green-600" data-testid="resumen-ingreso-total">
                      {formatCurrency(totalIngreso, selectedResultado.ingreso?.moneda)}
                    </span>
                  </button>
                  {showIngresoDetalle && hasGrupo && (
                    <div className="bg-slate-50/50 rounded px-3 py-1.5 mb-1">
                      {grupoIngresos.map(g => (
                        <div key={g.unidad_id} className="flex items-center justify-between py-1">
                          <span className="text-[10px] md:text-xs text-slate-500">{g.nombre} ({g.tipo})</span>
                          <span className="text-[10px] md:text-xs font-mono text-slate-600">
                            {g.consolidada
                              ? 'Incluida en la oficina'
                              : formatCurrency(g.ingreso_monto, selectedResultado.ingreso?.moneda)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <div>
              <button onClick={toggleEgresosDetalle} className="w-full flex items-center justify-between py-2 border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                <span className="text-xs md:text-sm text-slate-600 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></span>Egreso
                  {showEgresosDetalle ? <ChevronUp className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />}
                </span>
                <span className="text-sm md:text-base font-mono text-red-500">
                  {formatCurrency(selectedResultado.egreso?.monto || 0, selectedResultado.egreso?.moneda || 'ARS')}
                </span>
              </button>
              {showEgresosDetalle && (
                <div className="bg-slate-50/50 rounded px-3 py-1.5 mb-1">
                  {loadingEgresos ? (
                    <p className="text-[10px] text-slate-400 py-1">Cargando...</p>
                  ) : egresosDetalle ? (
                    <div className="space-y-0">
                      {[
                        { label: 'Retenciones', value: egresosDetalle.retenciones },
                        { label: 'IIBB (4%)', value: egresosDetalle.iibb },
                        { label: 'EDEMSA', value: egresosDetalle.edemsa, serv: 'edemsa' },
                        { label: 'Tasa Municipal', value: egresosDetalle.tasa_municipal, serv: 'tasa_municipal' },
                        { label: 'Agua', value: egresosDetalle.agua, serv: 'agua' },
                        { label: 'Expensas', value: egresosDetalle.expensas, alwaysShow: true },
                        { label: 'Gastos del Negocio', value: egresosDetalle.gastos_negocio },
                      ].filter(item => item.alwaysShow || item.value > 0).map(item => (
                        <div key={item.label} className="flex items-center justify-between py-1">
                          <span className="text-[10px] md:text-xs text-slate-500 flex items-center gap-1">
                            {item.label}
                            {(egresosDetalle.facturas?.[item.serv] || []).map((nombre, i) => (
                              <button key={i} type="button" onClick={() => abrirFacturaServicio(item.serv, i)} title={`Ver factura ${nombre || ''}`}
                                className="p-0.5 text-red-600 hover:text-red-800" data-testid={`inversor-factura-${item.serv}-${i}`}>
                                <FileText className="w-3.5 h-3.5" />
                              </button>
                            ))}
                          </span>
                          <span className="text-[10px] md:text-xs font-mono text-slate-600">{formatCurrency(item.value || 0, 'ARS')}</span>
                        </div>
                      ))}
                      {(egresosDetalle.gastos_negocio_detalle || []).length > 0 && (
                        <div className="pl-3 border-l border-slate-200 mb-1" data-testid="inversor-gastos-negocio-detalle">
                          {egresosDetalle.gastos_negocio_detalle.map((g, i) => (
                            <div key={i} className="flex items-center justify-between py-0.5">
                              <span className="text-[10px] md:text-[11px] text-slate-400">{g.concepto}</span>
                              <span className="text-[10px] md:text-[11px] font-mono text-slate-400">{formatCurrency(g.monto, 'ARS')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {Object.entries(egresosDetalle).every(([k, v]) => k === 'facturas' || k === 'gastos_negocio_detalle' || !v || v === 0) && (
                        <p className="text-[10px] text-slate-400 py-1">Sin detalle cargado</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-400 py-1">Sin detalle</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-50">
              <span className="text-xs md:text-sm text-slate-600 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></span>Fee administración
              </span>
              <span className="text-sm md:text-base font-mono text-red-500">
                {formatCurrency(selectedResultado.fee_administracion?.monto || 0, selectedResultado.fee_administracion?.moneda || 'ARS')}
              </span>
            </div>
            <div className="flex items-center justify-between py-2.5 mt-1 bg-[#1B4D3E]/5 rounded-md px-2.5 -mx-1">
              <span className="text-xs md:text-sm font-semibold text-slate-800">RENTA NETA</span>
              <span className="text-base md:text-lg font-bold font-mono text-[#1B4D3E]">
                {formatCurrency(gananciaActual?.monto, gananciaActual?.moneda)}
              </span>
            </div>
            {selectedResultado.propietario_porcentaje && selectedResultado.propietario_porcentaje < 100 && gananciaActual?.monto && (
              <div className="flex items-center justify-between py-2 px-2.5 -mx-1">
                <span className="text-[10px] md:text-xs text-slate-500">Tu participación ({selectedResultado.propietario_porcentaje}%)</span>
                <span className="text-sm md:text-base font-semibold font-mono text-[#1B4D3E]">
                  {formatCurrency(gananciaActual.monto * selectedResultado.propietario_porcentaje / 100, gananciaActual.moneda)}
                </span>
              </div>
            )}
          </div>

          {/* Documentos del período */}
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
            <div className="flex items-center justify-between p-2 bg-slate-50 rounded text-xs md:text-sm">
              <span className={selectedResultado.factura_alquiler_info ? "text-slate-600" : "text-slate-400"}>Factura Inquilino</span>
              {selectedResultado.factura_alquiler_info ? (
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" asChild>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/facturacion/factura-pdf/${selectedResultado.factura_alquiler_info.factura_id}`} target="_blank" rel="noopener noreferrer">
                      <Eye className="w-3 h-3 mr-0.5" />Ver
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" asChild>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/facturacion/factura-pdf/${selectedResultado.factura_alquiler_info.factura_id}`} download>
                      <Download className="w-3 h-3" />
                    </a>
                  </Button>
                </div>
              ) : (
                <span className="text-[10px] md:text-xs text-slate-400">No cargado</span>
              )}
            </div>
            <div className="flex items-center justify-between p-2 bg-slate-50 rounded text-xs md:text-sm">
              <span className="text-slate-600">Detalle Expensas</span>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] px-1.5"
                onClick={() => navigate(`/propietario/expensas/${unidadId}`)}
                data-testid="resumen-detalle-expensas-link"
              >
                Ver expensas <ExternalLink className="w-3 h-3 ml-0.5" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-8">
          <EmptyState icon={FileText} title="Sin resultados" description="Aún no hay resultados para esta unidad." />
        </div>
      )}
    </div>
  );

  const renderFacturacion = () => {
    const formatPeriodoCorto = (periodo) => {
      if (!periodo || typeof periodo !== 'string' || !periodo.includes('-')) return periodo || '—';
      const [y, m] = periodo.split('-');
      return `${m}/${(y || '').slice(2)}`;
    };

    return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm table-fixed" data-testid="facturacion-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-2 md:px-3 py-2 text-[10px] md:text-xs font-semibold text-slate-600 uppercase tracking-wider w-[15%]">Mes</th>
              <th className="text-right px-2 md:px-3 py-2 text-[10px] md:text-xs font-semibold text-slate-600 uppercase tracking-wider w-[35%]">Monto</th>
              <th className="text-center px-2 md:px-3 py-2 text-[10px] md:text-xs font-semibold text-slate-600 uppercase tracking-wider w-[25%]">Factura</th>
              <th className="text-center px-2 md:px-3 py-2 text-[10px] md:text-xs font-semibold text-slate-600 uppercase tracking-wider w-[25%]">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {resultados.map(r => {
              const ganancia = getGananciaNeta(r);
              const hasFactura = !!r.factura_propietario;
              const estado = hasFactura ? r.factura_propietario.estado : 'pendiente';
              return (
                <tr key={r.resultado_id} className="hover:bg-slate-50/50" data-testid={`factura-row-${r.periodo}`}>
                  <td className="px-2 md:px-3 py-2 text-xs text-slate-700 font-medium">
                    {formatPeriodoCorto(r.periodo)}
                  </td>
                  <td className="px-2 md:px-3 py-2 text-right font-mono text-xs text-slate-800">
                    {ganancia ? formatCurrency(ganancia.monto, ganancia.moneda) : '-'}
                  </td>
                  <td className="px-2 md:px-3 py-2 text-center">
                    {hasFactura ? (
                      <Button variant="outline" size="sm" className="h-6 text-[10px] px-1.5" asChild>
                        <a href={resultadosApi.downloadFacturaPropietario(r.resultado_id, previewMode ? previewPropietarioId : undefined)} download>
                          <Download className="w-3 h-3 mr-0.5" />Ver
                        </a>
                      </Button>
                    ) : previewMode ? (
                      <span className="text-[10px] text-slate-400">-</span>
                    ) : (
                      <>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" id={`upload-${r.resultado_id}`}
                          onChange={(e) => handleFileUpload(e, r.resultado_id)} />
                        <Button size="sm" className="h-6 text-[10px] px-1.5 bg-[#1B4D3E] hover:bg-[#163d32]"
                          disabled={uploadingFactura === r.resultado_id}
                          onClick={() => document.getElementById(`upload-${r.resultado_id}`)?.click()}>
                          {uploadingFactura === r.resultado_id
                            ? <span className="animate-pulse">...</span>
                            : <><Upload className="w-3 h-3 mr-0.5" />Subir</>}
                        </Button>
                      </>
                    )}
                  </td>
                  <td className="px-2 md:px-3 py-2 text-center">
                    {hasFactura ? (
                      estado === 'procesada'
                        ? <span className="inline-flex items-center gap-0.5 text-[10px] text-green-700 bg-green-50 px-1.5 py-0.5 rounded"><CheckCircle2 className="w-3 h-3" />OK</span>
                        : <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded"><Clock className="w-3 h-3" />Pend.</span>
                    ) : (
                      <span className="text-[10px] text-slate-400">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {resultados.length === 0 && (
          <div className="p-8">
            <EmptyState icon={Receipt} title="Sin períodos" description="Aún no hay períodos disponibles." />
          </div>
        )}
    </div>
    );
  };

  const renderRentabilidad = () => {
    const MONTH_NAMES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    const allPoints = resultados.slice(0, 12).reverse().map(r => {
      const rent = calcularRentabilidadAnualUSD(r);
      return { periodo: r.periodo, rentabilidad: rent };
    }).filter(d => d.rentabilidad !== null);

    const slots = [];
    // Use the latest published periodo as the end, not current month
    const lastPeriodo = resultados.length > 0 ? resultados[0].periodo : null;
    let endYear, endMonth;
    if (lastPeriodo) {
      const [y, m] = lastPeriodo.split('-').map(Number);
      endYear = y;
      endMonth = m;
    } else {
      const now = new Date();
      endYear = now.getFullYear();
      endMonth = now.getMonth() + 1;
    }
    for (let i = chartMonths - 1; i >= 0; i--) {
      let m = endMonth - i;
      let y = endYear;
      while (m <= 0) { m += 12; y--; }
      const p = `${y}-${String(m).padStart(2, '0')}`;
      const label = MONTH_NAMES[m - 1];
      const point = allPoints.find(dp => dp.periodo === p);
      slots.push({ periodo: p, label, rentabilidad: point?.rentabilidad ?? null });
    }

    const filledSlots = slots.filter(s => s.rentabilidad !== null);
    const maxRent = filledSlots.length > 0 ? Math.max(...filledSlots.map(d => d.rentabilidad)) : 10;
    const chartMax = Math.ceil(maxRent / 5) * 5 || 10;
    const chartHeight = 200;
    const ySteps = [0, Math.round(chartMax / 2), chartMax];

    // Build pixel-based points for consistent line width
    const chartWidth = 100; // percentage based
    const pointsWithCoords = slots.map((s, i) => ({
      ...s,
      x: (i + 0.5) / slots.length * 100,
      y: s.rentabilidad !== null ? ((s.rentabilidad / chartMax) * 100) : null,
    }));
    const validPoints = pointsWithCoords.filter(p => p.y !== null);

    return (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-3 md:px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs md:text-sm font-medium text-slate-700">Rentabilidad Anual (USD)</span>
          <div className="flex items-center gap-1">
            {[3, 6, 12].map(m => (
              <button key={m} onClick={() => setChartMonths(m)}
                className={cn(
                  "text-[10px] md:text-xs px-2 py-1 rounded font-medium transition-colors",
                  chartMonths === m ? "bg-[#1B4D3E] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                )}>{m}M</button>
            ))}
          </div>
        </div>
        <div className="px-3 md:px-5 py-4">
          <div className="relative">
            {/* Y axis */}
            <div className="absolute left-0 top-0 bottom-5 w-7 flex flex-col justify-between text-[9px] text-slate-400 font-mono">
              {ySteps.slice().reverse().map(v => <span key={v}>{v}%</span>)}
            </div>
            {/* Chart area */}
            <div className="ml-8 relative" style={{ height: chartHeight }}>
              {/* Horizontal grid lines only */}
              {ySteps.map(v => (
                <div key={v} className="absolute w-full border-t border-dashed border-slate-100"
                  style={{ bottom: `${(v / chartMax) * 100}%` }} />
              ))}
              {/* SVG line + area + dots */}
              <svg className="absolute inset-0 w-full h-full overflow-visible" viewBox={`0 0 1000 ${chartMax}`} preserveAspectRatio="none">
                <defs>
                  <linearGradient id="rentFill2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1B4D3E" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#1B4D3E" stopOpacity="0.01" />
                  </linearGradient>
                </defs>
                {validPoints.length >= 2 && (
                  <>
                    <polygon fill="url(#rentFill2)"
                      points={[
                        ...validPoints.map(p => `${p.x * 10},${chartMax - p.rentabilidad}`),
                        `${validPoints[validPoints.length - 1].x * 10},${chartMax}`,
                        `${validPoints[0].x * 10},${chartMax}`
                      ].join(' ')} />
                    <polyline fill="none" stroke="#1B4D3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      points={validPoints.map(p => `${p.x * 10},${chartMax - p.rentabilidad}`).join(' ')}
                      vectorEffect="non-scaling-stroke" />
                  </>
                )}
                {/* Average line */}
                {validPoints.length >= 1 && (() => {
                  const avg = validPoints.reduce((s, p) => s + p.rentabilidad, 0) / validPoints.length;
                  const avgY = chartMax - avg;
                  return (
                    <line x1="0" y1={avgY} x2="1000" y2={avgY}
                      stroke="#94a3b8" strokeWidth="1" strokeDasharray="6 4"
                      vectorEffect="non-scaling-stroke" />
                  );
                })()}
              </svg>
              {/* Average label */}
              {validPoints.length >= 1 && (() => {
                const avg = validPoints.reduce((s, p) => s + p.rentabilidad, 0) / validPoints.length;
                const avgPct = (avg / chartMax) * 100;
                return (
                  <div className="absolute right-0 pointer-events-none"
                    style={{ bottom: `${avgPct}%`, transform: 'translateY(50%)' }}>
                    <span className="text-[8px] md:text-[9px] font-mono text-slate-400 bg-white px-1">prom. {avg.toFixed(1)}%</span>
                  </div>
                );
              })()}
              {/* Dots as HTML for consistent shape */}
              {pointsWithCoords.map(p => p.y !== null ? (
                <div key={`dot-${p.periodo}`} className="absolute pointer-events-none"
                  style={{ left: `${p.x}%`, bottom: `${p.y}%`, transform: 'translate(-50%, 50%)' }}>
                  <div className="w-2 h-2 rounded-full bg-white border-[1.5px] border-[#1B4D3E]" />
                </div>
              ) : null)}
              {/* Value labels above dots */}
              {pointsWithCoords.map(p => p.y !== null ? (
                <div key={p.periodo} className="absolute text-center pointer-events-none"
                  style={{ left: `${p.x}%`, bottom: `${p.y}%`, transform: 'translate(-50%, -100%)', marginBottom: '8px' }}>
                  <span className="text-[9px] md:text-[10px] font-mono font-semibold text-[#1B4D3E]">{p.rentabilidad.toFixed(1)}%</span>
                </div>
              ) : null)}
            </div>
            {/* X axis */}
            <div className="ml-8 flex mt-1">
              {slots.map(s => (
                <span key={s.periodo} className="flex-1 text-center text-[9px] md:text-[10px] text-slate-400">{s.label}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInquilino = () => {
    const esConjunto = !!unidad?.estado_ocupacion_efectivo_label;
    const oficinas = unidad?.ocupacion_oficinas || [];
    const fmtFecha = (f) => f ? new Date(f + 'T12:00:00').toLocaleDateString('es-AR') : '-';
    return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 md:px-5 py-4 md:py-5 space-y-4">
        {esConjunto ? (
          <>
            <div>
              <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Ocupación interna</p>
              <p className="text-base md:text-lg font-semibold font-mono text-slate-900" data-testid="inquilino-ocupacion-label">
                {unidad.estado_ocupacion_efectivo_label}
              </p>
            </div>
            {oficinas.length > 0 ? (
              <div className="space-y-2">
                <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider">Oficinas alquiladas</p>
                <div className="space-y-2">
                  {oficinas.map((o, i) => (
                    <div key={i} className="border border-slate-200 rounded-md p-3 bg-slate-50/60" data-testid={`inquilino-oficina-${o.unidad_nombre}`}>
                      <p className="text-sm font-bold text-slate-900">{o.unidad_nombre}</p>
                      <p className="text-xs text-slate-600 mt-1">Inquilino: <span className="font-medium text-slate-800">{o.inquilino || '—'}</span></p>
                      <p className="text-xs text-slate-600">Contrato vigente hasta: <span className="font-medium text-slate-800">{fmtFecha(o.fecha_fin)}</span></p>
                      {o.cocheras?.length > 0 && (
                        <p className="text-xs text-slate-600">Cocheras: <span className="font-medium text-slate-800">{o.cocheras.join(', ')}</span></p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400" data-testid="inquilino-sin-contrato">Sin contrato vigente</p>
            )}
          </>
        ) : contrato ? (
          <>
            <div>
              <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Inquilino</p>
              <p className="text-sm md:text-base font-medium text-slate-900">
                {contrato?.inquilino || unidad.alquiler_resumen?.inquilino || 'Vacante'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Inicio contrato</p>
                <p className="text-sm font-medium text-slate-900">
                  {contrato.fecha_inicio ? new Date(contrato.fecha_inicio + 'T12:00:00').toLocaleDateString('es-AR') : '-'}
                </p>
              </div>
              <div>
                <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Fin contrato</p>
                <p className="text-sm font-medium text-slate-900">
                  {contrato.fecha_fin ? new Date(contrato.fecha_fin + 'T12:00:00').toLocaleDateString('es-AR') : '-'}
                </p>
              </div>
            </div>
            {contrato.monto_alquiler && (
              <div>
                <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Monto alquiler</p>
                <p className="text-sm font-semibold font-mono text-slate-900">
                  {formatCurrency(contrato.monto_alquiler.monto, contrato.monto_alquiler.moneda)}
                </p>
              </div>
            )}
            {contrato.indice_actualizacion && (
              <div>
                <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Índice actualización</p>
                <p className="text-sm font-medium text-slate-900 capitalize">{contrato.indice_actualizacion}</p>
              </div>
            )}
            {(() => {
              const etapas = contrato.etapas_contractuales || [];
              const docs = etapas.flatMap((e) =>
                (e.archivos || []).map((a) => ({
                  ...a,
                  etapa_id: e.etapa_id,
                  etapa_titulo: e.titulo || e.tipo || 'Documento',
                }))
              );
              return (
                <div className="pt-2">
                  <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                    Documentos del contrato
                  </p>
                  {docs.length > 0 ? (
                    <div className="space-y-1.5" data-testid="contrato-documentos-lista">
                      {docs.map((d) => (
                        <a
                          key={d.archivo_id}
                          href={contratosApi.downloadEtapaArchivo(contrato.contrato_id, d.etapa_id, d.archivo_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`descargar-doc-${d.archivo_id}`}
                          className="flex items-center gap-2 text-xs md:text-sm font-medium text-[#1B4D3E] bg-[#1B4D3E]/10 rounded px-3 py-2 hover:bg-[#1B4D3E]/20 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{d.nombre_original || 'Documento'}</span>
                          <span className="ml-auto text-[10px] text-slate-500 truncate max-w-[45%]">{d.etapa_titulo}</span>
                        </a>
                      ))}
                    </div>
                  ) : contrato.archivo_contrato_id ? (
                    <a
                      href={contratosApi.downloadArchivo(contrato.archivo_contrato_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="descargar-contrato-legacy"
                      className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-[#1B4D3E] bg-[#1B4D3E]/10 rounded px-3 py-1.5 hover:bg-[#1B4D3E]/20 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Descargar contrato
                    </a>
                  ) : (
                    <p className="text-xs text-slate-400" data-testid="contrato-sin-documentos">Sin documentos cargados</p>
                  )}
                </div>
              );
            })()}
          </>
        ) : (
          <p className="text-sm text-slate-400" data-testid="inquilino-sin-contrato">Sin contrato vigente</p>
        )}
      </div>
    </div>
    );
  };

  const renderInfoUnidad = () => (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 md:px-5 py-4 md:py-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Edificio</p>
            <p className="text-sm font-medium text-slate-900">{unidad.edificio_nombre}</p>
          </div>
          <div>
            <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Unidad</p>
            <p className="text-sm font-medium text-slate-900">{unidad.nombre_unidad}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Tipo</p>
            <p className="text-sm font-medium text-slate-900">{getStatusLabel(unidad.tipo)}</p>
          </div>
          <div>
            <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Tamaño</p>
            <p className="text-sm font-medium text-slate-900">{formatNumber(unidad.m2_cubiertos || unidad.tamano_m2 || 0)} m²</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Inversión</p>
            <p className="text-sm font-semibold font-mono text-slate-900">
              {unidad.monto_inversion?.monto ? formatCurrency(unidad.monto_inversion.monto, unidad.monto_inversion.moneda) : '-'}
            </p>
          </div>
          <div>
            <p className="text-[10px] md:text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Estado</p>
            {unidad.estado_ocupacion_efectivo_label ? (
              <div className="flex flex-col gap-1" data-testid="detalle-ocupacion">
                <StatusBadge status={unidad.estado_ocupacion_efectivo || 'vacante'} className="w-fit" />
                <span className="text-sm font-semibold text-[#1B4D3E] font-mono">{unidad.estado_ocupacion_efectivo_label}</span>
              </div>
            ) : (
              <StatusBadge status={unidad.estado_alquiler} />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const esCowork = unidad?.tipo === 'cowork';
  const tabs = esCowork
    ? [{ id: 'resumen', label: 'Resumen' }, { id: 'detalle', label: 'Detalle Mensual' }]
    : tabsBase;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - only desktop */}
      <div className="hidden md:block bg-white border-b border-slate-200 px-3 md:px-8 py-2.5">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">{unidad.edificio_nombre}</span>
          <span className="text-slate-300">·</span>
          <span className="text-sm font-medium text-slate-900">{esCowork ? 'Cowork' : unidad.nombre_unidad}</span>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="bg-white border-b border-slate-200 px-3 md:px-8">
        <div className="flex gap-0 overflow-x-auto scrollbar-none -mb-px">
          {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={cn(
                  "flex items-center gap-1.5 px-3 md:px-4 py-2.5 text-xs md:text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                  activeTab === tab.id
                    ? "border-[#1B4D3E] text-[#1B4D3E]"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                )}
              >
                {tab.label}
              </button>
            ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-3 md:px-8 py-3 md:py-5">
        {previewMode && selectedResultado && selectedResultado.estado !== 'publicado' && (
          <div
            data-testid="vista-inversor-borrador-banner"
            className="mb-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs md:text-sm text-amber-800"
          >
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>
              <b>Borrador</b> — el período {formatPeriodo ? formatPeriodo(selectedPeriodo) : selectedPeriodo} aún no está publicado. El inversor todavía no ve esta información.
            </span>
          </div>
        )}
        {esCowork ? (
          <CoworkInversorView edificioId={unidad.edificio_id} activeTab={activeTab} />
        ) : (
          <>
            {activeTab === 'resumen' && renderResumen()}
            {activeTab === 'facturacion' && renderFacturacion()}
            {activeTab === 'inquilino' && renderInquilino()}
            {activeTab === 'detalle' && <PestañaExpensas unidadId={unidadId} />}
            {activeTab === 'pagos' && <ExpensasEstadoCuenta unidadId={unidadId} />}
            {activeTab === 'estado_cuenta' && <ExpensasResumenDeuda unidadId={unidadId} />}
          </>
        )}
      </div>
    </div>
  );
};

export default PropietarioUnidadDetalle;

const PestañaExpensas = ({ unidadId }) => {
  const [periodos, setPeriodos] = useState([]);
  const [periodo, setPeriodo] = useState('');
  const [vista, setVista] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinPeriodo, setSinPeriodo] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSinPeriodo(false);
    (async () => {
      try {
        const r = await expensasApi.getMisPeriodosExpensas(unidadId);
        const data = r.data || [];
        setPeriodos(data);
        if (data.length) {
          setPeriodo(data[0].periodo);
        } else {
          setSinPeriodo(true);
          setLoading(false);
        }
      } catch (e) {
        setSinPeriodo(true);
        setLoading(false);
      }
    })();
  }, [unidadId]);

  useEffect(() => {
    if (sinPeriodo) { setLoading(false); return; }
    if (!periodo) return;
    setLoading(true);
    (async () => {
      try {
        const r = await expensasApi.getMiVistaExpensas(unidadId, periodo || undefined);
        if (r.data?.sin_periodo) { setSinPeriodo(true); setVista(null); }
        else setVista(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No se pudo cargar la vista de expensas');
        setVista(null);
      } finally { setLoading(false); }
    })();
  }, [unidadId, periodo, sinPeriodo]);

  if (sinPeriodo) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
        <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <div className="text-sm text-slate-600">Aún no hay expensas aprobadas para tu edificio.</div>
      </div>
    );
  }

  if (loading || !vista) {
    return <LoadingState text="Cargando expensas..." />;
  }

  const tot = vista.edificio?.totales || {};
  const rubros = vista.edificio?.rubros || [];
  const grupos = vista.mis_grupos || [];
  const sinGrupo = vista.mis_unidades_sin_grupo || [];
  const adminFull = !!vista.es_admin_full;

  const rubrosPorRubro = {};
  rubros.forEach((r) => {
    if (!rubrosPorRubro[r.rubro]) rubrosPorRubro[r.rubro] = [];
    rubrosPorRubro[r.rubro].push(r);
  });

  // Formatos visuales del Portal Propietario (solo visual, no afecta cálculos):
  // - Resumen: siempre positivo, sin decimales, redondeado, miles con punto.
  // - Detalle: mantiene signo, sin decimales, redondeado, miles con punto.
  const nfMiles = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtResumen = (v) => `$ ${nfMiles.format(Math.round(Math.abs(Number(v) || 0)))}`;
  const fmtDetalle = (v) => {
    const n = Math.round(Number(v) || 0);
    return n < 0 ? `-$ ${nfMiles.format(Math.abs(n))}` : `$ ${nfMiles.format(n)}`;
  };

  return (
    <div className="space-y-4">
      {/* Selector mes */}
      <div className="flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        <label className="text-xs text-slate-500 uppercase tracking-wide">Período</label>
        <select
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value)}
          data-testid="expensas-period-selector"
          className="text-sm h-8 px-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
        >
          {periodos.map((p) => (
            <option key={p.periodo_id} value={p.periodo}>{formatPeriodo(p.periodo)}</option>
          ))}
        </select>
      </div>

      {(() => {
        const respConfig = vista.responsable_config || {};
        const modo = respConfig.modo || 'contrato';
        const respFijoNombre = respConfig.fijo?.razon_social;
        // Caso 1: admin Full + modo fijo → la paga el responsable fijo (ej: GC SAS)
        if (adminFull && modo === 'fijo' && respFijoNombre) {
          return (
            <div className="bg-amber-50 border border-amber-300 rounded-md px-3 py-2 flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <b>Vista informativa.</b> Estas expensas las paga <b>{respFijoNombre}</b> y se descuentan de tu liquidación. No tenés que abonarlas.
              </div>
            </div>
          );
        }
        // Caso 2: admin Full sin responsable fijo definido
        if (adminFull) {
          return (
            <div className="bg-amber-50 border border-amber-300 rounded-md px-3 py-2 flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <b>Vista informativa.</b> Estas expensas las paga el inquilino bajo modalidad de administración full.
              </div>
            </div>
          );
        }
        // Caso 3: modo contrato + tiene unidad alquilada → la paga el inquilino
        const hayAlquilada = (vista.mis_grupos || []).some(g =>
          (g.items || []).some(it => (it.estado_unidad || '').toLowerCase() === 'alquilada')
        ) || (vista.mis_unidades_sin_grupo || []).some(it => (it.estado_unidad || '').toLowerCase() === 'alquilada');
        if (modo === 'contrato' && hayAlquilada) {
          return (
            <div className="bg-amber-50 border border-amber-300 rounded-md px-3 py-2 flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <b>Vista informativa.</b> Las expensas de tus unidades alquiladas las paga el inquilino directamente al consorcio.
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Card resumen mis unidades */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Tu liquidación</h3>
          <div className="text-right">
            <div className="text-xs text-slate-500">Total a {adminFull ? 'informar' : 'pagar'}</div>
            <div className="text-2xl font-bold text-slate-900">{fmtResumen(vista.total_general)}</div>
          </div>
        </div>
        <table className="w-full text-sm hidden md:table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b">
            <tr>
              <th className="py-2 px-3 text-left font-medium">Grupo / Unidad</th>
              <th className="py-2 px-3 text-right font-medium">$ Expensas</th>
              <th className="py-2 px-3 text-right font-medium">$ Cocheras</th>
              <th className="py-2 px-3 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {grupos.map((g) => (
              <React.Fragment key={g.grupo_id}>
                <tr className="bg-emerald-50 border-y border-emerald-200">
                  <td className="py-1.5 px-3 font-semibold text-emerald-900">
                    {g.nombre}
                    <span className="ml-2 text-[10px] uppercase text-emerald-700 font-normal">{g.items.length} {g.items.length === 1 ? 'unidad' : 'unidades'}</span>
                  </td>
                  <td className="py-1.5 px-3 text-right font-mono font-semibold text-emerald-900">{fmtResumen(g.total_expensas)}</td>
                  <td className="py-1.5 px-3 text-right font-mono font-semibold text-emerald-900">{fmtResumen(g.total_cocheras)}</td>
                  <td className="py-1.5 px-3 text-right font-mono font-bold text-emerald-900">{fmtResumen(g.total)}</td>
                </tr>
                {g.items.map((l) => (
                  <tr key={l.unidad_id} className="hover:bg-slate-50/60">
                    <td className="py-1 px-3 pl-8 text-slate-800"><span className="text-slate-300 mr-1.5">↳</span>{l.unidad_nombre}</td>
                    <td className="py-1 px-3 text-right font-mono">{fmtResumen(l.monto_expensas)}</td>
                    <td className="py-1 px-3 text-right font-mono">{fmtResumen(l.monto_cocheras)}</td>
                    <td className="py-1 px-3 text-right font-mono font-semibold">{fmtResumen(l.total)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {sinGrupo.map((l) => (
              <tr key={l.unidad_id} className="hover:bg-slate-50/60">
                <td className="py-1 px-3 font-medium text-slate-900">{l.unidad_nombre}</td>
                <td className="py-1 px-3 text-right font-mono">{fmtResumen(l.monto_expensas)}</td>
                <td className="py-1 px-3 text-right font-mono">{fmtResumen(l.monto_cocheras)}</td>
                <td className="py-1 px-3 text-right font-mono font-semibold">{fmtResumen(l.total)}</td>
              </tr>
            ))}
            {grupos.length === 0 && sinGrupo.length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-slate-400">Sin liquidaciones</td></tr>
            )}
          </tbody>
        </table>

        {/* Mobile: tarjetas por unidad funcional (sin árbol/grupo, sin scroll horizontal) */}
        <div className="md:hidden space-y-3" data-testid="expensas-mobile-cards">
          {grupos.map((g) => {
            const officeItem = (g.items || []).find((it) => Math.abs(it.monto_expensas || 0) > 0.005);
            const cocheraItems = (g.items || []).filter((it) => Math.abs(it.monto_cocheras || 0) > 0.005);
            const titulo = officeItem?.unidad_nombre || g.nombre;
            return (
              <div key={g.grupo_id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`expensas-card-${g.grupo_id}`}>
                <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-900 text-sm border-b border-slate-200">{titulo}</div>
                <div className="px-3 py-2 space-y-1.5 text-sm">
                  {Math.abs(g.total_expensas || 0) > 0.005 && (
                    <div className="flex justify-between gap-3">
                      <span className="text-slate-600">Expensas oficina</span>
                      <span className="font-mono text-slate-900">{fmtResumen(g.total_expensas)}</span>
                    </div>
                  )}
                  {cocheraItems.map((c) => (
                    <div key={c.unidad_id} className="flex justify-between gap-3">
                      <span className="text-slate-600">Cochera {c.unidad_nombre}</span>
                      <span className="font-mono text-slate-900">{fmtResumen(c.monto_cocheras)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between gap-3 pt-1.5 mt-1 border-t border-slate-200">
                    <span className="font-semibold text-slate-900">Total</span>
                    <span className="font-mono font-bold text-emerald-700">{fmtResumen(g.total)}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {sinGrupo.map((l) => {
            const tieneExp = Math.abs(l.monto_expensas || 0) > 0.005;
            const tieneCoch = Math.abs(l.monto_cocheras || 0) > 0.005;
            return (
              <div key={l.unidad_id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`expensas-card-${l.unidad_id}`}>
                <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-900 text-sm border-b border-slate-200">{l.unidad_nombre}</div>
                <div className="px-3 py-2 space-y-1.5 text-sm">
                  {tieneExp && (
                    <div className="flex justify-between gap-3"><span className="text-slate-600">Expensas oficina</span><span className="font-mono text-slate-900">{fmtResumen(l.monto_expensas)}</span></div>
                  )}
                  {tieneCoch && (
                    <div className="flex justify-between gap-3"><span className="text-slate-600">Cochera</span><span className="font-mono text-slate-900">{fmtResumen(l.monto_cocheras)}</span></div>
                  )}
                  <div className="flex justify-between gap-3 pt-1.5 mt-1 border-t border-slate-200">
                    <span className="font-semibold text-slate-900">Total</span>
                    <span className="font-mono font-bold text-emerald-700">{fmtResumen(l.total)}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {grupos.length === 0 && sinGrupo.length === 0 && (
            <div className="py-6 text-center text-slate-400 text-sm">Sin liquidaciones</div>
          )}
        </div>
      </div>

      {/* Detalle del edificio */}
      <details className="bg-white border border-slate-200 rounded-lg" open>
        <summary className="cursor-pointer px-4 py-3 border-b border-slate-200 text-sm font-semibold text-slate-900">
          Detalle del edificio este mes
        </summary>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
              <div className="text-[10px] uppercase text-slate-500">$ Expensas</div>
              <div className="text-sm font-mono font-semibold">{fmtResumen(tot.expensas)}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
              <div className="text-[10px] uppercase text-slate-500">$ Cocheras</div>
              <div className="text-sm font-mono font-semibold">{fmtResumen(tot.cocheras)}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
              <div className="text-[10px] uppercase text-slate-500">Fondo reserva</div>
              <div className="text-sm font-mono font-semibold">{fmtResumen(tot.fondo_reserva)}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2">
              <div className="text-[10px] uppercase text-emerald-700">Total edificio</div>
              <div className="text-sm font-mono font-bold text-emerald-900">{fmtResumen(tot.importe_total)}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b">
                <tr>
                  <th className="py-1.5 px-3 text-left font-medium">Subrubro</th>
                  <th className="py-1.5 px-3 text-right font-medium whitespace-nowrap w-px">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.entries(rubrosPorRubro).map(([rubro, items]) => {
                  const subTot = items.reduce((s, i) => s + Number(i.importe || 0), 0);
                  return (
                    <React.Fragment key={rubro}>
                      <tr className="bg-slate-100/70">
                        <td colSpan={2} className="py-1 px-3 font-bold text-slate-800">{rubro}</td>
                      </tr>
                      {items.map((r, idx) => (
                        <tr key={`${rubro}-${idx}`}>
                          <td className="py-0.5 px-3 pl-5 text-slate-600 break-words">{r.subrubro}</td>
                          <td className="py-0.5 px-3 text-right font-mono whitespace-nowrap w-px">{fmtDetalle(r.importe)}</td>
                        </tr>
                      ))}
                      <tr className="bg-emerald-50/40 border-y border-emerald-100">
                        <td className="py-0.5 px-3 text-[10px] uppercase font-semibold text-emerald-700">Subtotal</td>
                        <td className="py-0.5 px-3 text-right font-mono font-semibold text-emerald-900 whitespace-nowrap w-px">{fmtDetalle(subTot)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </details>
    </div>
  );
};
